import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaTrophy, FaFlag, FaUsers, FaGavel, FaMedal, FaUser as FaUserIcon, FaUserEdit, FaArrowLeft, FaExclamationTriangle } from '../components/Icons';
import { FaUserShield, FaUserLock, FaUserCog, FaCalendarAlt, FaCalculator, FaRobot, FaForward, FaBook, FaImage, FaExclamationCircle } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebaseConfig';
import { signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { theme } from '../theme';
import { UserProfile } from '../models/authTypes';

const SidebarContainer = styled.div`
  width: 280px;
  background-color: ${theme.colors.primary.main};
  color: ${theme.colors.primary.contrastText};
  padding: 0;
  box-shadow: ${theme.shadows.medium};
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 10;
  transition: ${theme.transitions.normal};
`;

const Logo = styled.div`
  font-size: 1.6rem;
  font-weight: ${theme.typography.fontWeights.bold};
  text-align: center;
  padding: ${theme.spacing.xl} ${theme.spacing.lg};
  background-color: ${theme.colors.primary.dark};
  color: ${theme.colors.primary.contrastText};
  letter-spacing: 1px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  
  span.vr {
    color: ${theme.colors.secondary.main};
    font-weight: ${theme.typography.fontWeights.bold};
  }
  
  span.simracers {
    color: ${theme.colors.primary.contrastText};
  }
`;

const MenuList = styled.ul`
  list-style: none;
  padding: ${theme.spacing.md} 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.primary.dark};
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.primary.light};
    border-radius: 4px;
  }
`;

const MenuItem = styled.li<{ $active?: boolean }>`
  margin: 0;
  padding: 0;
  
  a {
    display: flex;
    align-items: center;
    padding: ${theme.spacing.md} ${theme.spacing.lg};
    color: ${props => props.$active ? theme.colors.secondary.main : theme.colors.primary.contrastText};
    text-decoration: none;
    font-weight: ${props => props.$active ? theme.typography.fontWeights.bold : theme.typography.fontWeights.medium};
    transition: ${theme.transitions.normal};
    border-left: 4px solid ${props => props.$active ? theme.colors.secondary.main : 'transparent'};
    background-color: ${props => props.$active ? 'rgba(255, 255, 255, 0.05)' : 'transparent'};
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
      color: ${theme.colors.secondary.main};
    }
    
    svg {
      margin-right: ${theme.spacing.md};
      font-size: 1.2rem;
    }
  }
`;

const MenuSection = styled.div`
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const MenuSectionTitle = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.sizes.xs};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: ${theme.typography.fontWeights.bold};
`;

const UserSection = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background-color: ${theme.colors.primary.dark};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${theme.colors.primary.light};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${theme.spacing.md};
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserInfo = styled.div`
  flex: 1;
  overflow: hidden;
`;

const UserName = styled.div`
  color: ${theme.colors.primary.contrastText};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.fontWeights.semibold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.sizes.xs};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AuthButton = styled.button<{ className?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${theme.spacing.md};
  background-color: ${props => props.className === 'logout' ? '#c62828' : theme.colors.secondary.main};
  color: ${theme.colors.secondary.contrastText};
  border: none;
  border-radius: ${theme.borderRadius.small};
  font-weight: ${theme.typography.fontWeights.medium};
  cursor: pointer;
  transition: ${theme.transitions.normal};
  margin-top: ${theme.spacing.md};
  
  &:hover {
    background-color: ${props => props.className === 'logout' ? '#a90000' : '#1e2340'};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background-color: ${theme.colors.text.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    margin-right: ${theme.spacing.sm};
    font-size: ${theme.typography.sizes.md};
  }
`;

const Sidebar: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const { currentUser, hasPermission } = useAuth();
  
  // Verificar si el usuario tiene alguno de los roles requeridos
  const hasAnyRole = (roles: string[]): boolean => {
    if (!currentUser) return false;
    return roles.includes(currentUser.role);
  };
  
  // Verificar si el usuario tiene permiso para ver un elemento del menú
  const hasMenuPermission = (requiredPermission: string): boolean => {
    // Si el usuario es superadmin, tiene acceso a todo
    if (currentUser?.role === 'superadmin') return true;
    
    // Verificar si el usuario tiene el permiso requerido
    return hasPermission(requiredPermission);
  };
  
  // Verificar si la ruta está activa (incluye subrutas)
  const isActive = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };
  
  const handleLogin = async () => {
    try {
      setIsLoading(true);
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      setIsLoading(true);
      await signOut(auth);
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <SidebarContainer>
      <Logo>
        <span className="vr">VR</span>
        <span className="simracers">SIMRACERS</span>
      </Logo>
      
      <MenuList>
        {/* Ligas y Competición */}
        {hasMenuPermission('view:leagues') && (
          <MenuItem $active={isActive('/ligas')}>
            <Link to="/ligas">
              <FaTrophy /> Ligas
            </Link>
          </MenuItem>
        )}
        
        {hasMenuPermission('view:races') && (
          <MenuItem $active={isActive('/carreras')}>
            <Link to="/carreras">
              <FaFlag /> Carreras
            </Link>
          </MenuItem>
        )}
        
        {hasMenuPermission('view:standings') && (
          <MenuItem $active={isActive('/clasificaciones')}>
            <Link to="/clasificaciones">
              <FaMedal /> Clasificaciones
            </Link>
          </MenuItem>
        )}
        
        {hasMenuPermission('view:lastres') && (
          <MenuItem $active={location.pathname.includes('/lastres')}>
            <Link to="/lastres">
              <FaCalculator /> Lastres
            </Link>
          </MenuItem>
        )}
        
        {hasMenuPermission('view:teams') && (
          <MenuItem $active={isActive('/equipos')}>
            <Link to="/equipos">
              <FaUsers /> Equipos
            </Link>
          </MenuItem>
        )}
        
        {/* Pilotos */}
        {hasMenuPermission('view:drivers') && (
          <MenuItem $active={isActive('/socios')}>
            <Link to="/socios">
              <FaUserIcon /> Pilotos
            </Link>
          </MenuItem>
        )}
        
        {/* Sanciones, Reclamaciones y Apelaciones */}
        {hasMenuPermission('view:penalties') && (
          <MenuItem $active={isActive('/sanciones') && !isActive('/sanciones/reglas-automaticas')}>
            <Link to="/sanciones">
              <FaGavel /> Sanciones
            </Link>
          </MenuItem>
        )}
        
        {hasMenuPermission('view:penalty_rules') && (
          <MenuItem $active={isActive('/sanciones/reglas-automaticas')}>
            <Link to="/sanciones/reglas-automaticas">
              <FaRobot /> Reglas Automáticas
            </Link>
          </MenuItem>
        )}
        
        {/* Blog Page Link - Movido debajo de Redacción IA */}

        {hasMenuPermission('view:claims') && (
          <MenuItem $active={isActive('/reclamaciones')}>
            <Link to="/reclamaciones">
              <FaExclamationCircle /> Reclamaciones
            </Link>
          </MenuItem>
        )}
        
        {hasMenuPermission('view:appeals') && (
          <MenuItem $active={isActive('/apelaciones')}>
            <Link to="/apelaciones">
              <FaExclamationTriangle /> Apelaciones
            </Link>
          </MenuItem>
        )}
        
        {/* Otros */}
        {hasMenuPermission('view:events') && (
          <MenuItem $active={isActive('/eventos')}>
            <Link to="/eventos">
              <FaCalendarAlt /> Eventos
            </Link>
          </MenuItem>
        )}
        
        {hasMenuPermission('view:points') && (
          <MenuItem $active={isActive('/puntuaciones')}>
            <Link to="/puntuaciones">
              <FaCalculator /> Sistemas de Puntos
            </Link>
          </MenuItem>
        )}

        {/* Redacción IA */}
        {hasMenuPermission('view:ia_redaccion_circuitos') && (
          <MenuItem $active={isActive('/ia/redaccion-circuitos')}>
            <Link to="/ia/redaccion-circuitos">
              <FaRobot /> Redacción IA
            </Link>
          </MenuItem>
        )}
        
        {/* Blog Page Link */}
        {hasMenuPermission('view:blog') && (
          <MenuItem $active={isActive('/blog')}>
            <Link to="/blog">
              <FaBook /> Blog
            </Link>
          </MenuItem>
        )}
        {/* WarmUp */}
        {hasMenuPermission('view:warmup') && (
          <MenuItem $active={isActive('/warmup')}>
            <Link to="/warmup">
              <FaForward /> WarmUp
            </Link>
          </MenuItem>
        )}
        
        {/* Guía */}
        {hasMenuPermission('view:guide') && (
          <MenuItem $active={isActive('/guia-bienvenida')}>
            <Link to="/guia-bienvenida">
              <FaBook /> Guía Primavera 2025
            </Link>
          </MenuItem>
        )}
        
        {/* Miniaturas */}
        {hasAnyRole(['admin', 'superadmin']) && (
          <MenuItem $active={isActive('/miniaturas/generador-miniaturas')}>
            <Link to="/miniaturas/generador-miniaturas">
              <FaImage /> Miniaturas
            </Link>
          </MenuItem>
        )}
        {/* Administración */}
        {hasAnyRole(['admin', 'superadmin']) && (
          <MenuSection>
            <MenuSectionTitle>Administración</MenuSectionTitle>
            {/* Gestión de Usuarios */}
            {hasMenuPermission('admin:users') && (
              <MenuItem $active={isActive('/admin/usuarios')}>
                <Link to="/admin/usuarios">
                  <FaUserShield />
                  <span>Usuarios</span>
                </Link>
              </MenuItem>
            )}
            
            {/* Gestión de Roles */}
            <MenuItem $active={isActive('/admin/roles')}>
              <Link to="/admin/roles">
                <FaUserCog />
                <span>Gestión de Roles</span>
              </Link>
            </MenuItem>
            
            {/* Comentado hasta que se implemente la gestión de permisos */}
            {/* <MenuItem $active={isActive('/admin/permisos')}>
              <Link to="/admin/permisos">
                <FaUserLock />
                <span>Gestión de Permisos</span>
              </Link>
            </MenuItem> */}
          </MenuSection>
        )}
      </MenuList>
      
      <UserSection>
        {currentUser ? (
          <>
            <UserAvatar>
              {currentUser.photoURL ? (
                <img src={currentUser.photoURL} alt={currentUser.displayName || 'Usuario'} />
              ) : (
                <FaUserIcon />
              )}
            </UserAvatar>
            <UserInfo>
              <UserName>{currentUser.displayName || 'Usuario'}</UserName>
              <UserEmail>{currentUser.email || 'Sin email'}</UserEmail>
              <AuthButton
                className="logout"
                onClick={handleLogout}
                disabled={isLoading}
              >
                <FaArrowLeft /> Cerrar sesión
              </AuthButton>
            </UserInfo>
          </>
        ) : (
          <AuthButton
            onClick={handleLogin}
            disabled={isLoading}
          >
            <FaUserEdit /> Iniciar sesión
          </AuthButton>
        )}
      </UserSection>
    </SidebarContainer>
  );
};

export default Sidebar;
